/**
 * Блок «Индекс потребительских цен» (US-11 FR-6Б, решение 6 от 07.09).
 *
 * Четыре строки в фиксированном порядке: всего · продовольственные ·
 * непродовольственные · платные услуги. Значение — индекс в процентах к
 * соответствующему периоду прошлого года (FR-6Б.2).
 *
 * Заголовок колонки — «Индекс, %» (FR-6Б.3). В прототипе колонка
 * подписана «Доля, %», что неверно по смыслу: 106,5 — индекс, а не доля,
 * и сумма по строкам к 100 % не сходится и сходиться не должна.
 *
 * ИПЦ в районном разрезе органы статистики не публикуют — он считается по
 * области и по городам. Поэтому при `level="oblast"` рядом с заголовком
 * стоит явная пометка «по области» (FR-6Б.4), чтобы областная цифра не
 * читалась как районная.
 */
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';

export type CpiRowKey = 'total' | 'food' | 'nonFood' | 'services';

/** Порядок строк фиксирован (FR-6Б.1). */
const ROW_KEYS: CpiRowKey[] = ['total', 'food', 'nonFood', 'services'];

interface Props {
  /**
   * Индекс по строкам, % к соответствующему периоду прошлого года.
   * Пусто/undefined по строке — значения нет; ни одного значения →
   * «нет данных» по блоку (FR-6Б.4).
   */
  values?: Partial<Record<CpiRowKey, number | null>>;
  /**
   * Уровень, к которому относится индекс. 'oblast' — рядом с заголовком
   * пометка «по области» (FR-6Б.4).
   */
  level?: 'district' | 'oblast';
  /** Отчётный период индекса — свой (FR-6Б.5, FR-7.2). */
  periodLabel?: string | null;
}

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v);

export const CpiTable = ({
  values,
  level = 'oblast',
  periodLabel,
}: Props) => {
  const { t } = useTranslation();

  const rows = ROW_KEYS.map((key) => ({
    key,
    value: values?.[key] ?? null,
  }));
  const isEmpty = rows.every((row) => row.value === null);

  return (
    <Card className="shadow-card">
      <CardHeader className="space-y-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="flex flex-wrap items-baseline gap-2">
            <CardTitle>{t('digitalPassport.cpi.title')}</CardTitle>
            {level === 'oblast' && (
              // Пометка уровня: областной индекс не должен читаться как районный.
              <span
                data-testid="cpi-level-badge"
                className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-text-muted"
              >
                {t('digitalPassport.cpi.levelOblast')}
              </span>
            )}
          </span>
          {periodLabel != null && (
            <span className="text-sm text-text-muted" data-testid="cpi-period">
              {periodLabel}
            </span>
          )}
        </div>
        <p className="text-sm text-text-muted">{t('digitalPassport.cpi.hint')}</p>
      </CardHeader>
      <CardContent>
        <section data-testid="block-cpi">
          {isEmpty ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-surface-border pb-2 text-xs text-text-muted">
                <span className="min-w-0 flex-1">
                  {t('digitalPassport.cpi.colName')}
                </span>
                <span className="w-20 shrink-0 text-right">
                  {t('digitalPassport.cpi.colIndex')}
                </span>
              </div>
              <ul data-testid="cpi-list">
                {rows.map(({ key, value }, index) => (
                  <li
                    key={key}
                    data-testid={`cpi-row-${key}`}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-1 py-2.5 text-sm',
                      index % 2 === 1 && 'bg-surface-muted/60',
                    )}
                  >
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate',
                        key === 'total' && 'font-bold',
                      )}
                    >
                      {t(`digitalPassport.cpi.rows.${key}`)}
                    </span>
                    <span
                      className={cn(
                        'w-20 shrink-0 text-right tabular-nums',
                        key === 'total' && 'font-bold',
                      )}
                    >
                      {value !== null ? fmt1(value) : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
